/**
 * RFQ Create Wizard Step Components
 *
 * Extracted from RFQCreateWizard.tsx - renders individual wizard steps.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import {
  Plus,
  Trash2,
} from 'lucide-react';

import type {
  BasicDetailsForm,
  LineItemForm,
  AdditionalCostForm,
  RequiredCertificateForm,
} from './rfqWizardTypes';
import NDAUploadCard from './NDAUploadCard';

// Steps 5 (Suppliers) and 6 (Review) live in their own module; re-exported
// here so `import { ... } from './RFQWizardSteps'` keeps working.
export { SupplierSelectionStep, ReviewStep } from './RFQWizardStepsSelection';

/** CR-5466: admin-managed RFQ Type option surfaced in the dropdown. */
export interface RFQTypeOption {
  id: number;
  name: string;
  enabled: boolean;
}

/* ------------------------------------------------------------------ */
/*  Step 1: Basic Details (CR-5466 rebuild)                            */
/* ------------------------------------------------------------------ */

interface Step1Props {
  basicDetails: BasicDetailsForm;
  setBasicDetails: (details: BasicDetailsForm) => void;
  /** Admin-managed dropdown values fetched from /api/v1/rfq/types/ */
  rfqTypes: RFQTypeOption[];
  /** Current user's company name — surfaced as "Customer" read-only */
  customerName: string;
  /** Current user's display name — surfaced as "Raised By" read-only */
  raisedByName: string;
}

export function BasicDetailsStep({
  basicDetails,
  setBasicDetails,
  rfqTypes,
  customerName,
  raisedByName,
}: Step1Props) {
  // CR-5466: design has a 2-column layout — left column = identifying
  // metadata (RFQ ID, Customer, Reference, RFQ Type, dates, Raised By,
  // NDA toggle); right column = RFQ Description big textarea.
  const enabledTypes = rfqTypes.filter((t) => t.enabled);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Left column — RFQ metadata */}
      <div className="space-y-4">
        <div>
          <Label>RFQ ID</Label>
          <Input value="Will be auto-generated on save" disabled readOnly className="bg-muted/30" />
        </div>

        <div>
          <Label>Customer</Label>
          <Input value={customerName || '—'} disabled readOnly className="bg-muted/30" />
        </div>

        <div>
          <Label htmlFor="reference">Reference</Label>
          <Input
            id="reference"
            value={basicDetails.reference}
            onChange={(e) => setBasicDetails({ ...basicDetails, reference: e.target.value })}
            placeholder="e.g. REF 0389472"
          />
        </div>

        <div>
          <Label>RFQ Type</Label>
          <select
            value={basicDetails.rfq_type ?? ''}
            onChange={(e) => setBasicDetails({
              ...basicDetails,
              rfq_type: e.target.value ? parseInt(e.target.value, 10) : null,
            })}
            className="w-full p-2 border rounded-md bg-background text-foreground"
          >
            <option value="">— Select RFQ Type —</option>
            {enabledTypes.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {enabledTypes.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              No RFQ Types configured. An admin can add them in Admin Settings &rarr; Platform.
              This field is optional — the RFQ can be raised without one.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="rfq_date">RFQ Date</Label>
            <Input
              id="rfq_date"
              type="date"
              value={basicDetails.issue_date}
              onChange={(e) => setBasicDetails({ ...basicDetails, issue_date: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="rfq_deadline">RFQ Deadline *</Label>
            <Input
              id="rfq_deadline"
              type="date"
              value={basicDetails.response_deadline}
              onChange={(e) => setBasicDetails({ ...basicDetails, response_deadline: e.target.value })}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>

        <div>
          <Label>Raised By</Label>
          <Input value={raisedByName || '—'} disabled readOnly className="bg-muted/30" />
        </div>

        <div className="flex items-center space-x-2 pt-2">
          <Checkbox
            id="nda"
            checked={basicDetails.nda_required}
            onCheckedChange={(checked) => setBasicDetails({ ...basicDetails, nda_required: !!checked })}
          />
          <Label htmlFor="nda">Non-Disclosure Agreement Required?</Label>
        </div>
      </div>

      {/* Right column — RFQ Description */}
      <div className="space-y-2">
        <Label htmlFor="description">RFQ Description *</Label>
        <Textarea
          id="description"
          value={basicDetails.description}
          onChange={(e) => setBasicDetails({ ...basicDetails, description: e.target.value })}
          placeholder="Detailed requirements and specifications..."
          rows={18}
          className="resize-none"
        />
        {/* This line used to promise a document upload "in the Documents step",
            which that step has never had. Pointing instead at the Documents tab
            on the created RFQ would have been just as untrue: the API exposes
            RFQDocumentViewSet, but no screen on either side offers an upload
            control. Until one does, the honest thing is to promise nothing and
            say where the requirements actually go. */}
        <p className="text-xs text-muted-foreground">
          State the full requirement here — suppliers see this description in the RFQ.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step 2: Line Items                                                 */
/* ------------------------------------------------------------------ */

interface Step2Props {
  lineItems: LineItemForm[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, field: keyof LineItemForm, value: string | number | null) => void;
}

export function LineItemsStep({ lineItems, onAdd, onRemove, onUpdate }: Step2Props) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Line Items</h3>
        <Button type="button" variant="outline" size="sm" onClick={onAdd}>
          <Plus className="h-4 w-4 mr-1" /> Add Item
        </Button>
      </div>

      {lineItems.map((item, index) => (
        <Card key={index}>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-sm">Line {index + 1}</CardTitle>
              {lineItems.length > 1 && (
                <Button variant="ghost" size="sm" onClick={() => onRemove(index)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Description *</Label>
              <Input
                value={item.item_description}
                onChange={(e) => onUpdate(index, 'item_description', e.target.value)}
                placeholder="Item description"
              />
            </div>
            <div>
              <Label className="text-xs">Part Number *</Label>
              <Input
                value={item.part_number}
                onChange={(e) => onUpdate(index, 'part_number', e.target.value)}
                placeholder="PN-001"
              />
            </div>
            <div>
              <Label className="text-xs">Quantity *</Label>
              <Input
                type="number"
                value={item.quantity}
                onChange={(e) => onUpdate(index, 'quantity', parseInt(e.target.value) || 0)}
                min={1}
              />
            </div>
            <div>
              <Label className="text-xs">Unit</Label>
              <Input
                value={item.unit_of_measure}
                onChange={(e) => onUpdate(index, 'unit_of_measure', e.target.value)}
                placeholder="Each"
              />
            </div>
            <div>
              <Label className="text-xs">Target Unit Price</Label>
              <Input
                type="number"
                value={item.target_unit_price || ''}
                onChange={(e) => onUpdate(index, 'target_unit_price', e.target.value ? parseFloat(e.target.value) : null)}
                placeholder="0.00"
                step="0.01"
              />
            </div>
            {/* CR-5467: Delivery Location + Required Date moved off the
                line item — they belong at RFQ header level. The header
                form add lands in the follow-up; for now the wizard
                exposes a single header-level Required Date via the
                response_deadline (already present at Step 1). */}
            <div>
              <Label className="text-xs">Drawing Issue / Revision</Label>
              <Input
                value={item.drawing_issue}
                onChange={(e) => onUpdate(index, 'drawing_issue', e.target.value)}
                placeholder="e.g. Rev B, Issue 3"
              />
            </div>
            <div className="col-span-3">
              <Label className="text-xs">Technical Specifications</Label>
              <Textarea
                value={item.technical_specifications}
                onChange={(e) => onUpdate(index, 'technical_specifications', e.target.value)}
                placeholder="Technical specs..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step 3: Additional Costs                                           */
/* ------------------------------------------------------------------ */

interface Step3Props {
  costs: AdditionalCostForm[];
  currency: string;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, field: keyof AdditionalCostForm, value: string | number) => void;
}

export function AdditionalCostsStep({ costs, currency, onAdd, onRemove, onUpdate }: Step3Props) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-semibold">Additional Costs</h3>
          <p className="text-sm text-muted-foreground">Tooling, packaging, transport costs (optional)</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onAdd}>
          <Plus className="h-4 w-4 mr-1" /> Add Cost
        </Button>
      </div>

      {costs.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8 text-muted-foreground">
            No additional costs added. Click &quot;Add Cost&quot; to include tooling, packaging, or transport costs.
          </CardContent>
        </Card>
      ) : (
        costs.map((cost, index) => (
          <Card key={index}>
            <CardContent className="pt-4 grid grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Cost Type</Label>
                <select
                  value={cost.cost_type}
                  onChange={(e) => onUpdate(index, 'cost_type', e.target.value)}
                  className="w-full p-2 border rounded-md text-sm"
                >
                  <option value="tooling">Tooling</option>
                  <option value="packaging">Packaging</option>
                  <option value="transport">Transport</option>
                  <option value="setup">Setup</option>
                  <option value="certification">Certification</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Description</Label>
                <Input
                  value={cost.description}
                  onChange={(e) => onUpdate(index, 'description', e.target.value)}
                  placeholder="Cost description"
                />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs">Amount ({currency})</Label>
                  <Input
                    type="number"
                    value={cost.amount}
                    onChange={(e) => onUpdate(index, 'amount', parseFloat(e.target.value) || 0)}
                    step="0.01"
                  />
                </div>
                <Button variant="ghost" size="sm" className="mt-5" onClick={() => onRemove(index)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step 4: Documents & Certificates                                   */
/* ------------------------------------------------------------------ */

interface Step4Props {
  certificates: RequiredCertificateForm[];
  ndaRequired: boolean;
  /** The NDA file picked so far (only meaningful when ndaRequired). */
  ndaFile: File | null;
  onNdaFileChange: (file: File | null) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, field: keyof RequiredCertificateForm, value: string | number | boolean) => void;
}

export function DocumentsStep({ certificates, ndaRequired, ndaFile, onNdaFileChange, onAdd, onRemove, onUpdate }: Step4Props) {
  return (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="font-semibold">Required Certificates</h3>
            <p className="text-sm text-muted-foreground">Certificates suppliers must provide</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={onAdd}>
            <Plus className="h-4 w-4 mr-1" /> Add Certificate
          </Button>
        </div>

        {certificates.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8 text-muted-foreground">
              No certificates required. Click &quot;Add Certificate&quot; to require ISO, material certs, etc.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {certificates.map((cert, index) => (
              <Card key={index}>
                <CardContent className="pt-4 grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Type</Label>
                    <select
                      value={cert.certificate_type}
                      onChange={(e) => onUpdate(index, 'certificate_type', e.target.value)}
                      className="w-full p-2 border rounded-md text-sm"
                    >
                      <option value="iso_9001">ISO 9001</option>
                      <option value="iso_14001">ISO 14001</option>
                      <option value="iso_45001">ISO 45001</option>
                      <option value="reach">REACH</option>
                      <option value="rohs">RoHS</option>
                      <option value="ce_marking">CE Marking</option>
                      <option value="material_cert">Material Certificate</option>
                      <option value="test_cert">Test Certificate</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input
                      value={cert.certificate_name}
                      onChange={(e) => onUpdate(index, 'certificate_name', e.target.value)}
                      placeholder="Certificate name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Validity (days)</Label>
                    <Input
                      type="number"
                      value={cert.expiry_tolerance_days}
                      onChange={(e) => onUpdate(index, 'expiry_tolerance_days', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={cert.is_mandatory}
                        onCheckedChange={(checked) => onUpdate(index, 'is_mandatory', !!checked)}
                      />
                      <Label className="text-xs">Mandatory</Label>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => onRemove(index)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {ndaRequired && <NDAUploadCard file={ndaFile} onChange={onNdaFileChange} />}
    </div>
  );
}
